import React, { useState, useEffect } from 'react';
import designApi from '@services/designApi.jsx';
import ResourceUploadField from '@components/common/ResourceUploadField.jsx';

const ModalSetting = ({ themeId, themePageMaster }) => {
  // 상태 관리
  const [modalPage, setModalPage] = useState({
    themePageMaster: themePageMaster,
    languageCode: 'ko',
    overlayBackgroundColor: '#000000',
    overlayBackgroundOpacity: 0.5,
    modalBackgroundColor: '#FFFFFF',
    modalBorderRadius: 10,
    modalWidth: 400,
    modalHeight: 300,
    closeButtonPositionX: 350,
    closeButtonPositionY: 50,
    titleText: '',
    titleColor: '#000000',
    contentText: '',
    contentColor: '#000000',
    confirmButtonText: '확인',
    confirmButtonColor: '#007BFF',
    confirmButtonTextColor: '#FFFFFF',
    cancelButtonText: '취소',
    cancelButtonColor: '#6C757D',
    cancelButtonTextColor: '#FFFFFF'
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // API 호출로 데이터 가져오기
  useEffect(() => {
    const fetchModalPage = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const data = await designApi.getModalPage(themeId);
        
        setModalPage({
          ...modalPage,
          ...data
        });

        console.log("API 호출 성공:", data);
      } catch (err) {
        console.error("API 호출 실패:", err);
        setError(err);
        // 에러 발생 시 기본 데이터 유지
      } finally {
        setIsLoading(false);
      }
    };

    fetchModalPage();
  }, [themeId]);

  // 입력값 변경 핸들러
  const handleInputChange = (field, value) => {
    setModalPage(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // 리소스 업로드 처리 함수
  const handleResourceUploaded = (resourceType, resourceId) => {
    setModalPage({
      ...modalPage,
      [resourceType]: resourceId
    });
  };
  
  // 오버레이 투명도 조절 핸들러
  const handleOpacityChange = (value) => {
    // 0.0 ~ 1.0 범위로 유지
    const opacity = Math.min(1.0, Math.max(0.0, parseFloat(value) || 0.0));
    setModalPage({
      ...modalPage,
      overlayBackgroundOpacity: opacity
    });
  };

  // 닫기 버튼 위치 중앙 정렬
  const handleCenterAlign = () => {
    const centerX = (modalPage.modalWidth || 400) - 25;
    const centerY = 25;
    
    setModalPage({
      ...modalPage,
      closeButtonPositionX: centerX,
      closeButtonPositionY: centerY
    });
  };
  
  // 저장 이벤트 리스너 (페이지 상위 컴포넌트에서 저장 요청 시 실행)
  useEffect(() => {
    const eventName = `savePageDesign_modal`; // 각 컴포넌트마다 고유한 이벤트 이름

    const saveHandler = async (event) => {
      try {
        // 저장 중임을 표시
        setIsLoading(true);

        // 자체 로직을 사용하여 데이터 저장
        const dataToSave = {
          ...modalPage,
          themeId: event.detail.themeId // themeId를 명시적으로 포함
        };
        const isSucceed = await designApi.saveModalPage(event.detail.themeId, dataToSave);

        if(isSucceed) {
          // 성공 메시지
          alert("저장되었습니다.");
        } else {
          // 실패 메시지
          alert("저장에 실패했습니다.");
        }

      } catch (error) {
        // 오류 처리
        console.error("저장 실패:", error);
        alert("저장 중 오류가 발생했습니다.");
      } finally {
        setIsLoading(false);
      }
    };

    // 이벤트 리스너 등록
    document.addEventListener(eventName, saveHandler);

    // 컴포넌트 언마운트 시 리스너 제거
    return () => {
      document.removeEventListener(eventName, saveHandler);
    };
  }, [modalPage, themeId]); // 의존성 배열에 필요한 상태 포함

  return (
    <div className="design-settings">
      {/* 언어 선택 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">언어 선택</h4>
        <div className="option-select-container">
          <select
            className="option-select"
            value={modalPage.languageCode || "ko"}
            onChange={(e) =>
              handleInputChange('languageCode', e.target.value)
            }
          >
            <option value="ko">한국어</option>
            <option value="en">영어</option>
            <option value="jp">일본어</option>
            <option value="cn">중국어</option>
          </select>
          <i className="ri-arrow-down-s-line select-arrow"></i>
        </div>
      </div>

      {/* 배경 오버레이 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">배경 오버레이</h4>
        <div className="settings-row">
          <div className="settings-label">색상</div>
          <div className="color-picker-container">
            <input
              type="text"
              className="color-input"
              value={modalPage.overlayBackgroundColor || "#000000"}
              readOnly
            />
            <input
              type="color"
              className="color-picker"
              value={modalPage.overlayBackgroundColor || "#000000"}
              onChange={(e) => handleInputChange('overlayBackgroundColor', e.target.value)}
            />
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-label">투명도</div>
          <div className="opacity-control-container">
            <input
              type="range"
              className="opacity-slider"
              min="0"
              max="1"
              step="0.1"
              value={modalPage.overlayBackgroundOpacity || 0.5}
              onChange={(e) => handleOpacityChange(e.target.value)}
            />
            <span className="opacity-value">{Math.round((modalPage.overlayBackgroundOpacity || 0.5) * 100)}%</span>
          </div>
        </div>
      </div>

      {/* 모달 배경 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">모달 배경</h4>
        <div className="settings-row">
          <div className="settings-label">배경 이미지</div>
          <ResourceUploadField
            label=""
            resourceId={modalPage.modalBackgroundResourceId}
            onResourceUploaded={(id) =>
              handleResourceUploaded("modalBackgroundResourceId", id)
            }
            acceptTypes="image/*"
          />
        </div>
        <div className="settings-row">
          <div className="settings-label">배경 색상</div>
          <div className="color-picker-container">
            <input
              type="text"
              className="color-input"
              value={modalPage.modalBackgroundColor || "#FFFFFF"}
              readOnly
            />
            <input
              type="color"
              className="color-picker"
              value={modalPage.modalBackgroundColor || "#FFFFFF"}
              onChange={(e) => handleInputChange('modalBackgroundColor', e.target.value)}
            />
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-label">테두리 둥글기</div>
          <div className="settings-input">
            <input
              type="number"
              placeholder="10"
              value={modalPage.modalBorderRadius || 10}
              onChange={(e) => handleInputChange('modalBorderRadius', parseInt(e.target.value) || 0)}
            />
            <span className="unit-text">px</span>
          </div>
        </div>
      </div>

      {/* 모달 크기 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">모달 크기</h4>
        <div className="settings-row">
          <div className="settings-label">너비</div>
          <div className="settings-input">
            <input
              type="number"
              placeholder="400"
              value={modalPage.modalWidth || 400}
              onChange={(e) => handleInputChange('modalWidth', parseInt(e.target.value) || 400)}
            />
            <span className="unit-text">px</span>
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-label">높이</div>
          <div className="settings-input">
            <input
              type="number"
              placeholder="300"
              value={modalPage.modalHeight || 300}
              onChange={(e) => handleInputChange('modalHeight', parseInt(e.target.value) || 300)}
            />
            <span className="unit-text">px</span>
          </div>
        </div>
      </div>

      {/* 닫기 버튼 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">닫기 버튼</h4>
        <ResourceUploadField
          label=""
          resourceId={modalPage.closeButtonResourceId}
          onResourceUploaded={(id) =>
            handleResourceUploaded("closeButtonResourceId", id)
          }
          acceptTypes="image/*"
        />
        <div className="settings-row">
          <div className="settings-label">위치</div>
          <div className="position-component">
            <div className="position-input-group">
              <span className="position-label">X</span>
              <input
                type="text"
                className="position-input"
                value={modalPage.closeButtonPositionX || 0}
                onChange={(e) =>
                  setModalPage({
                    ...modalPage,
                    closeButtonPositionX: parseInt(e.target.value) || 0,
                  })
                }
              />
              <span className="position-label">Y</span>
              <input
                type="text"
                className="position-input"
                value={modalPage.closeButtonPositionY || 0}
                onChange={(e) =>
                  setModalPage({
                    ...modalPage,
                    closeButtonPositionY: parseInt(e.target.value) || 0,
                  })
                }
              />
            </div>
            <div className="direction-buttons">
              <div className="direction-row">
                <button
                  className="direction-btn"
                  data-direction="up"
                  onClick={() =>
                    setModalPage({
                      ...modalPage,
                      closeButtonPositionY: (modalPage.closeButtonPositionY || 0) - 1,
                    })
                  }
                >
                  <i className="ri-arrow-up-s-fill"></i>
                </button>
              </div>
              <div className="direction-row">
                <button
                  className="direction-btn"
                  data-direction="left"
                  onClick={() =>
                    setModalPage({
                      ...modalPage,
                      closeButtonPositionX: (modalPage.closeButtonPositionX || 0) - 1,
                    })
                  }
                >
                  <i className="ri-arrow-left-s-fill"></i>
                </button>
                <button
                  className="direction-btn"
                  data-direction="right"
                  onClick={() =>
                    setModalPage({
                      ...modalPage,
                      closeButtonPositionX: (modalPage.closeButtonPositionX || 0) + 1,
                    })
                  }
                >
                  <i className="ri-arrow-right-s-fill"></i>
                </button>
              </div>
              <div className="direction-row">
                <button
                  className="direction-btn"
                  data-direction="down"
                  onClick={() =>
                    setModalPage({
                      ...modalPage,
                      closeButtonPositionY: (modalPage.closeButtonPositionY || 0) + 1,
                    })
                  }
                >
                  <i className="ri-arrow-down-s-fill"></i>
                </button>
              </div>
            </div>
            <button
              className="btn center-btn"
              onClick={handleCenterAlign}
            >
              우상단정렬
            </button>
          </div>
        </div>
      </div>

      {/* 모달 제목 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">모달 제목</h4>
        <div className="settings-row">
          <div className="settings-input">
            <input 
              type="text" 
              placeholder="제목을 입력해주세요" 
              className="text-input"
              value={modalPage.titleText || ""}
              onChange={(e) => handleInputChange('titleText', e.target.value)}
            />
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-label">색상</div>
          <div className="color-picker-container">
            <input 
              type="text" 
              className="color-input" 
              value={modalPage.titleColor || "#000000"} 
              readOnly 
            />
            <input 
              type="color" 
              className="color-picker" 
              value={modalPage.titleColor || "#000000"}
              onChange={(e) => handleInputChange('titleColor', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* 모달 내용 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">모달 내용</h4>
        <div className="settings-row">
          <div className="settings-input">
            <textarea 
              placeholder="내용을 입력해주세요" 
              className="text-area"
              rows="3"
              value={modalPage.contentText || ""}
              onChange={(e) => handleInputChange('contentText', e.target.value)}
            />
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-label">색상</div>
          <div className="color-picker-container">
            <input 
              type="text" 
              className="color-input" 
              value={modalPage.contentColor || "#000000"} 
              readOnly 
            />
            <input 
              type="color" 
              className="color-picker" 
              value={modalPage.contentColor || "#000000"}
              onChange={(e) => handleInputChange('contentColor', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* 확인 버튼 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">확인 버튼</h4>
        <div className="settings-row">
          <div className="settings-label">버튼 이미지</div>
          <ResourceUploadField
            label=""
            resourceId={modalPage.confirmButtonResourceId}
            onResourceUploaded={(id) =>
              handleResourceUploaded("confirmButtonResourceId", id)
            }
            acceptTypes="image/*"
          />
        </div>
        <div className="settings-row">
          <div className="settings-label">버튼 텍스트</div>
          <div className="settings-input">
            <input 
              type="text" 
              placeholder="확인" 
              value={modalPage.confirmButtonText || "확인"}
              onChange={(e) => handleInputChange('confirmButtonText', e.target.value)}
            />
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-label">버튼 색상</div>
          <div className="color-picker-container">
            <input 
              type="text" 
              className="color-input" 
              value={modalPage.confirmButtonColor || "#007BFF"} 
              readOnly 
            />
            <input 
              type="color" 
              className="color-picker" 
              value={modalPage.confirmButtonColor || "#007BFF"}
              onChange={(e) => handleInputChange('confirmButtonColor', e.target.value)}
            />
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-label">텍스트 색상</div>
          <div className="color-picker-container">
            <input 
              type="text" 
              className="color-input" 
              value={modalPage.confirmButtonTextColor || "#FFFFFF"} 
              readOnly 
            />
            <input 
              type="color" 
              className="color-picker" 
              value={modalPage.confirmButtonTextColor || "#FFFFFF"}
              onChange={(e) => handleInputChange('confirmButtonTextColor', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* 취소 버튼 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">취소 버튼</h4>
        <div className="settings-row">
          <div className="settings-label">버튼 이미지</div>
          <ResourceUploadField
            label=""
            resourceId={modalPage.cancelButtonResourceId}
            onResourceUploaded={(id) =>
              handleResourceUploaded("cancelButtonResourceId", id)
            }
            acceptTypes="image/*"
          />
        </div>
        <div className="settings-row">
          <div className="settings-label">버튼 텍스트</div>
          <div className="settings-input">
            <input 
              type="text" 
              placeholder="취소" 
              value={modalPage.cancelButtonText || "취소"}
              onChange={(e) => handleInputChange('cancelButtonText', e.target.value)}
            />
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-label">버튼 색상</div>
          <div className="color-picker-container">
            <input 
              type="text" 
              className="color-input" 
              value={modalPage.cancelButtonColor || "#6C757D"} 
              readOnly 
            />
            <input 
              type="color" 
              className="color-picker" 
              value={modalPage.cancelButtonColor || "#6C757D"}
              onChange={(e) => handleInputChange('cancelButtonColor', e.target.value)}
            />
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-label">텍스트 색상</div>
          <div className="color-picker-container">
            <input 
              type="text" 
              className="color-input" 
              value={modalPage.cancelButtonTextColor || "#FFFFFF"} 
              readOnly 
            />
            <input 
              type="color" 
              className="color-picker" 
              value={modalPage.cancelButtonTextColor || "#FFFFFF"}
              onChange={(e) => handleInputChange('cancelButtonTextColor', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* 효과음 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">효과음</h4>
        <ResourceUploadField
          label="모달 팝업 효과음"
          resourceId={modalPage.popupSoundResourceId}
          onResourceUploaded={(id) =>
            handleResourceUploaded("popupSoundResourceId", id)
          }
          acceptTypes="audio/*"
        />
        <ResourceUploadField
          label="버튼 클릭 효과음"
          resourceId={modalPage.buttonClickSoundResourceId}
          onResourceUploaded={(id) =>
            handleResourceUploaded("buttonClickSoundResourceId", id)
          }
          acceptTypes="audio/*"
        />
      </div>
    </div>
  );
};

export default ModalSetting;